import * as React from 'react'

import { Box, SearchField, Spinner, Table } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import isEmpty from 'lodash/isEmpty'

import { observer } from 'mobx-react'

import { ExplotableList } from 'modules/core/components/GroupingExplotable'
import type { FilterOption as MultiFilterOption } from 'modules/core/components/MultiFilters/types'
import createComputed from 'modules/core/hooks/createComputed'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'
import users from 'modules/core/store/collections/users'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employeesCollection from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import Filters from 'modules/job_catalog/components/Filters'
import useEmployeesMultifilters, {
  filterFields,
} from 'modules/job_catalog/hooks/useEmployeesMultiFilters'
import type { Role } from 'modules/job_catalog/hooks/useRole'
import { employeeJobLevel } from 'modules/job_catalog/store/collections/levels'
import { employeeJobRole } from 'modules/job_catalog/store/collections/roles'
import Loading from 'modules/layout/components/Page/Loading'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import ExpandableGroup from './ExpandableGroup'
import Row from './Row'

const getEmployeeUser = (employee: Employee) => {
  const userId = employee.attributes.access_id
  const user = users.get(userId)
  if (!user) {
    throw Error(`Error getting users with id = ${userId}`)
  }
  return user
}

interface Props {
  role: Role
  contractVersions: ContractVersions
}

const useFilteredEmployees = createComputed((employees: Employee[], search: string) => {
  return employees.filter((employee) => {
    const user = getEmployeeUser(employee)
    return (
      isEmpty(search) || matchText(search, user.firstName) || matchText(search, user.fullName())
    )
  })
})

const RoleEmployeesTable: React.FC<Props> = ({ role, contractVersions }) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const levels =
    role.levels && role.levels.filter((x) => !x.isDefault).sort((a, b) => a.order - b.order)

  const {
    filteredEmployees,
    onApplyFilters,
    getFilterQuantity,
    applyDefaultFilters,
    getAppliedFilter,
  } = useEmployeesMultifilters(
    LOCAL_STORAGE_KEYS.jobCatalogEmployeesFilter,
    employeesCollection.toArray()
  )

  const { loading: arePoliciesLoading } = useAsyncData(async () => {
    await policyAuthorizations.authorizePolicies('contracts.see_gross_salary', filteredEmployees)
  }, [filteredEmployees.length])

  useUnsafeEffect(() => {
    applyDefaultFilters()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const employeesToShow = useFilteredEmployees(filteredEmployees, debouncedSearch).get()

  const employeesPerLevel = useUnsafeMemo(
    () => (levelId: number) =>
      employeesToShow.filter(
        (x) => employeeJobLevel(contractVersions, x)?.attributes.id === levelId
      ),
    [employeesToShow]
  )

  const [fields, setFields] = React.useState<readonly MultiFilterOption[]>([])

  const { loading } = useAsyncData(async () => {
    const fields = await filterFields()

    setFields(fields)
  }, [])

  if (loading) {
    return <Loading />
  }

  const roleEmployees = employeesToShow.filter(
    (x) => employeeJobRole(contractVersions, x)?.id === role.id
  )

  const showSalaryAmountColumn = roleEmployees.some((x) =>
    policyAuthorizations.for('contracts.see_gross_salary', x)
  )

  const rows = (employees: Employee[], level: { id: number; name: string }) =>
    employees.map((x) => (
      <Row
        level={level}
        key={x.attributes.id}
        employee={x}
        showSalaryAmountColumn={showSalaryAmountColumn}
      />
    ))

  const levelGroups =
    levels.length &&
    levels
      .sort((a, b) => a.order - b.order)
      .map((x) => {
        const employees = employeesPerLevel(x.id)

        return (
          <ExpandableGroup key={x.id} count={employees.length} level={x}>
            {rows(employees, x)}
          </ExpandableGroup>
        )
      })

  const defaultLevel = role.levels.find((x) => x.isDefault)
  const defaultLevelEmployees = defaultLevel && employeesPerLevel(defaultLevel.id)

  const rowForDefaultLevel =
    defaultLevel && defaultLevelEmployees?.length ? (
      rows(defaultLevelEmployees, defaultLevel)
    ) : (
      <Table.Blank colSpan={2} text={i18n.t('job_catalog.no_employees_assigned')} />
    )

  const options = () => (
    <Box width="full" flexDirection="row" alignItems="center" gap="s8">
      <Box>
        <Filters
          fields={fields}
          onApplyFilters={(filter) => onApplyFilters(employeesCollection.toArray(), filter)}
          getFilterQuantity={getFilterQuantity}
          getAppliedFilter={getAppliedFilter}
        />
      </Box>
      <Box width="full">
        <SearchField
          variant="big"
          search={search}
          onSearch={onSearch}
          placeholder={i18n.t('common.search')}
        />
      </Box>
    </Box>
  )

  if (arePoliciesLoading)
    return (
      <Box alignItems="center">
        <Spinner />
      </Box>
    )

  return (
    <Table overflowY="auto" noBottomRadius options={options} fullWidth>
      <Table noRadius hideBorder>
        <Table.Head>
          <Table.Row>
            <Table.HeadCell colSpan={4}>{i18n.t('employee_form.first_name')}</Table.HeadCell>
            <Table.HeadCell colSpan={4}>{i18n.t('employee_form.last_name')}</Table.HeadCell>
            <Table.HeadCell colSpan={4}>{i18n.t('employees.tenure')}</Table.HeadCell>
            <Table.HeadCell colSpan={4}>{i18n.t('common.manager')}</Table.HeadCell>
            {showSalaryAmountColumn && (
              <Table.HeadCell colSpan={4}>{i18n.t('job_catalog.salary_amount')}</Table.HeadCell>
            )}
            <Table.HeadCell colSpan={2} />
          </Table.Row>
        </Table.Head>
      </Table>
      <Table noRadius hideBorder>
        {!!levels.length ? (
          <ExplotableList>{levelGroups}</ExplotableList>
        ) : (
          <Table noRadius hideBorder>
            <Table.Body>{rowForDefaultLevel}</Table.Body>
          </Table>
        )}
      </Table>
    </Table>
  )
}

export default observer(RoleEmployeesTable)
